/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import { discovery } from '../services/Discovery';
import { logging } from '../services/LoggingAPI';

/**
 * @description
 * Contains App Configs associated to a particular client
 *
 * nameSpace values - common || dropinName || providerName
 * config values - promises
 *
 * appConfig obj structure:
 * {
 *    common: {
 *     configName1: value,
 *    },
 *    dropinName1: {
 *      configName1: value,
 *      configName2: value,
 *    },
 *    dropinName2: {
 *      configName1: value,
 *    },
*     providerName1: {
 *      configName1: value,
 *    },
 *   ...
 * }
 */
const appConfig = {};

/**
 * @description
 * Validating if nameSpace value is a -
 * 1. dropin
 * 2. provider
 * 3. 'common'
 *
 * @param {*} nameSpace
 * @returns boolean
 */
function _isValidNameSpace(nameSpace) {
  return nameSpace in discovery.dropins || nameSpace in discovery.providers || nameSpace === 'common';
}

/**
 * @description
 * Set App Config values for multiple namespace
 * @memberof AppConfigUtil
 * @param {*} configs - { dropinName1: { configName1: value, }, }
 */
function setAppConfigs(configs) {
  Object.keys(configs).forEach(nameSpace => {
    // nameSpace values - common || dropinName || providerName
    if (!_isValidNameSpace(nameSpace)) {
      logging.getLogger('AppConfigUtil').warn('Cannot set config as dropin / provider not found in discovery: ', nameSpace);
      return;
    }

    if (!appConfig[nameSpace]) {
      appConfig[nameSpace] = {};
    }

    appConfig[nameSpace] = {
      ...appConfig[nameSpace],
      ...configs[nameSpace],
    };
  });
}

/**
 * @description
 * Set App Config values for a namespace
 * @memberof AppConfigUtil
 * @param {*} nameSpace
 * @param {*} configObject - { configName1: configValue1, configName2: configValue2 }
 */
function setAppConfig(nameSpace, configObject) {
  setAppConfigs({
    [nameSpace]: configObject,
  });
}

/**
 * @description
 * Retrieves the configuration value for a given configName within a specified namespace
 * @memberof AppConfigUtil
 * @param {*} nameSpace
 * @param {*} configName
 *
 * @returns {Promise<*>} A Promise that resolves to the config value, or false if not found
 */
async function getAppConfigProperty(nameSpace, configName) {
  return appConfig[nameSpace]?.[configName] ?? false;
}

/**
 * @description
 * Retrieves all configuration values for a specified namespace
 * @memberof AppConfigUtil
 * @param {*} nameSpace
 *
 * @returns {Object} An object containing all configurations for the namespace, with config values as promises
 */
function getAppConfig(nameSpace) {
  return appConfig[nameSpace] ?? {};
}

export {
  setAppConfigs,
  setAppConfig,
  getAppConfig,
  getAppConfigProperty,
};
